/**
 * CadenceStepSectionPure
 *
 * Pure presentational component for the cadence step editor.
 * Receives all data and callbacks via props, no server dependencies.
 *
 * Editor Render Props (all optional - defaults to built-in components):
 * - renderSubjectEditor: Override the subject editor (defaults to TipTapInput)
 * - renderBodyEditor: Override the body editor (defaults to TipTapInput)
 * - renderReminderInput: Override the reminder selector (defaults to ManualSelectPure)
 * - renderSelectDropdown: Override email dropdowns (From, To)
 * - renderSuggestTemplates: Template suggestions (optional)
 * - renderCharCounter: Character counter for connect messages (optional)
 *
 * When render props are not provided:
 * - TipTapInput is used for subject/body with @ variable and / document mentions
 * - ManualSelectPure is used for reminder selection (requires reminderActions prop)
 *
 * Reminder Data Props (used when renderReminderInput not provided):
 * - reminderActions: Available reminder actions
 * - isLoadingReminderActions: Loading state
 * - onCreateReminderAction: Callback to create new reminder
 * - isCreatingReminderAction: Creating state
 */
import type { Editor } from '@tiptap/react';
import type { ReactNode, RefObject } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UserAtVariables } from '@poseidon/types/types/atVariables.type';

import type { TipTapDocument, TipTapVariable } from '../Input2/TipTapInput';
import type { ReminderAction } from '../ManualSelect';
import type { PreviousEmailThread } from '../ContactDetail/previousEmailThread';
import type { MessageAttachmentState } from './MessageAttachmentButtonPure';
import { Badge } from '../Badge';
import { cn } from '../cn';
import { ESC_PRIORITY, useEscStack } from '../contexts';
import Button from '../Input2/Button';
import CheckboxButton from '../Input2/CheckboxButton';
import ButtonGroup from '../Input2/ButtonGroup';
import ButtonSegment from '../Input2/ButtonSegment';
import FilterSelect from '../Input2/FilterSelect';
import Input from '../Input2/Input';
import TipTapInput from '../Input2/TipTapInput';
import { ManualSelectPure } from '../ManualSelect';
import { UpdateStepTypePopup } from '../Popup';
import { StepIcon } from '../StepIcon';
import { Arrows, General } from '../StrokeIcons';
import { Tooltip } from '../Tooltip';
import { WaveSpinner } from '../WaveSpinner';
import { MessageAttachmentButtonPure } from './MessageAttachmentButtonPure';

const USER_AT_VARS_SET = new Set<string>(UserAtVariables);

// ============================================================================
// Types
// ============================================================================

export type CadenceStepType =
  | 'message'
  | 'connect'
  | 'email'
  | 'inmail'
  | 'follow'
  | 'visit'
  | 'likeRecentPosts'
  | 'reminder'
  | 'call'
  | 'voice_note'
  | 'video_note'
  | 'sift'
  | 'switch_campaign'
  | 'switch';

export type SendMode = 'now' | 'scheduled';

export type StepChanges = {
  from?: string;
  to?: string;
  subject?: string;
  body2?: string;
  body?: string;
  cc?: string;
  bcc?: string;
  postsToLike?: number;
  onlyOpenLink?: boolean;
  useEmailSignature?: boolean;
  replyToPrevious?: boolean;
  type?: CadenceStepType;
};

export type StepData = {
  id: string;
  type: CadenceStepType;
  subject: string | null;
  body2: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  bcc: string | null;
  onlyOpenLink?: boolean;
  useEmailSignature?: boolean;
  /**
   * Email only: continue the contact's previous email thread instead of starting a new one. Leave
   * it unset to take the composer's default (`lastStepWasEmail`); set it to pin the toggle open or
   * closed regardless — the reply-inline composer pins it ON, because you clicked reply on a real
   * message. Either way the toggle only renders when `previousEmailThread` is non-null, i.e. there
   * is actually a thread to continue.
   */
  replyToPrevious?: boolean;
};

export type EditorDocument = {
  id: string;
  title?: string;
  type?: string;
  isPrivate?: boolean;
  imageUrl?: string;
};

export type EmailOption = {
  value: string;
  label: string;
  helperText?: string;
  primary?: boolean;
  lastUsed?: boolean;
};

export type AiChoice = {
  subject?: string;
  body: string;
};

export type StepListItem = {
  value: CadenceStepType;
  label: string;
  labelFull: ReactNode;
};

export type ContactForStep = {
  id: string;
  primaryFields?: {
    professionalEmail?: { data?: string | null } | null;
    phone?: { data?: string | null } | null;
  } | null;
};

export type ContactUserForStep = {
  liConnectedAt?: string | Date | null;
  emailCredentialId?: string | null;
};

export type UserTeamForStep = {
  teamId: string;
  userId: string;
  isPremium: boolean;
  useEmailSignatureByDefault?: boolean;
  useContactEmailAsBcc?: boolean;
  unipileAccountId?: string | null;
};

// ============================================================================
// Main Component Props
// ============================================================================

export type CadenceStepSectionPureProps = {
  // Core data
  userTeam: UserTeamForStep;
  contact: ContactForStep;
  contactUser: ContactUserForStep;
  step: StepData;
  atVariables: Record<string, string>;

  // Server data (passed from wrapper)
  documents?: EditorDocument[];
  fromEmailOptions?: EmailOption[];
  toEmailOptions?: EmailOption[];
  /**
   * The email thread this user can continue with the contact, or null when there is none. Non-null
   * is the gate for the "Reply in thread" toggle; its `subject` (which may be null) is what the
   * reply inherits and what the thread banner shows. Derive it with `getPreviousEmailThread`.
   *
   * The gate used to be `previousEmailSubject != null`, which quietly made the SUBJECT decide: a
   * thread whose latest email had none offered no toggle at all, even though the email was sitting
   * right there on the timeline. Threading rides on the provider ids, never on the subject.
   */
  previousEmailThread?: PreviousEmailThread | null;
  /**
   * Whether the last step run with this contact was that email. When it was, the rep opening the
   * email composer is continuing that exchange, so "Reply in thread" starts ON. Derive it with
   * `wasLastStepEmail`. An explicit `step.replyToPrevious` still wins (the reply-inline composer
   * forces it on), and the rep's own toggle wins over both.
   */
  lastStepWasEmail?: boolean;

  // Reminder actions (for reminder step type)
  reminderActions?: ReminderAction[];
  isLoadingReminderActions?: boolean;
  onCreateReminderAction?: (name: string) => Promise<void>;
  isCreatingReminderAction?: boolean;

  // AI state
  aiChoices?: AiChoice[];
  aiIndex?: number;
  aiCommand?: string;
  aiLoading?: boolean;

  // Step list for header
  stepsList?: StepListItem[];

  // Config
  allowEditStepType?: boolean;
  allowSaveAsTemplate?: boolean;
  allowEmailDetails?: boolean;
  disableLikeRecentPosts?: boolean;
  autoGenerateAi?: boolean;
  initiallyAnimate?: boolean;

  // Send mode
  sendMode?: SendMode;
  showSendModeDropdown?: boolean;
  onSendModeChange?: (mode: SendMode) => void;

  // File attachment (message/inmail only). Omitted → no attach button rendered.
  messageAttachment?: MessageAttachmentState;

  // Texts
  texts?: {
    improve?: string;
    generate?: string;
    submit?: string;
  };

  // Loading states
  isSubmitting?: boolean;
  isAiGenerating?: boolean;
  isSavingSnippet?: boolean;

  // Callbacks
  onSubmit: (step: StepData) => Promise<void>;
  onCancel?: () => void;
  onChange: (changes: StepChanges) => void;
  onGenerateAi?: (empty?: boolean) => Promise<void>;
  onAiCommandChange?: (command: string) => void;
  onAiIndexChange?: (index: number) => void;
  onSaveSnippet?: () => Promise<void>;
  onToast?: (message: string, type: 'error' | 'success') => void;

  // Render props for platform-specific components
  renderSubjectEditor?: (props: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    documents: EditorDocument[];
    atVariables: Record<string, string>;
    contactId: string;
    allowLinks: boolean;
    autoFocus: boolean;
    ref: RefObject<Editor | null>;
  }) => ReactNode;

  renderBodyEditor?: (props: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    documents: EditorDocument[];
    atVariables: Record<string, string>;
    contactId: string;
    allowFormatting: boolean;
    allowLinks: boolean;
    autoFocus: boolean;
    onFocus?: () => void;
    ref: RefObject<Editor | null>;
  }) => ReactNode;

  renderSuggestTemplates?: (props: {
    value: string;
    atVariables: Record<string, string>;
    documents: EditorDocument[];
    onClick: (body2: string, subject?: string) => void;
    forceShow: boolean;
  }) => ReactNode;

  renderReminderInput?: (props: { value: string; onChange: (value: string) => void; teamId: string }) => ReactNode;

  renderSelectDropdown?: (props: {
    options: EmailOption[];
    value?: string;
    onChange: (option: { value: string; label: string }) => void;
    placeholder: string;
    leftTitle?: string;
    children: ReactNode;
    emptyContent?: ReactNode;
    size?: 'small' | 'large';
    optionRow?: (option: EmailOption) => ReactNode;
  }) => ReactNode;

  renderCharCounter?: (props: { body2: string }) => ReactNode;

  // Styles
  wrapperClassName?: string;
  contentClassName?: string;
};

// ============================================================================
// Helper Functions
// ============================================================================

function isNotEmpty(value: string | null | undefined): boolean {
  return value != null && value.trim() !== '';
}

function isNotEmptyTipTap(value: string | null | undefined): boolean {
  if (!value) return false;
  const stripped = value.replace(/<[^>]*>/g, '').trim();
  return stripped.length > 0;
}

/**
 * Convert atVariables Record to TipTapVariable array for TipTapInput
 */
export function convertAtVariablesToTipTap(atVariables: Record<string, string>): TipTapVariable[] {
  return Object.entries(atVariables).map(([id, label]) => ({
    id,
    label: label || `@${id}`,
    group: USER_AT_VARS_SET.has(id) || id === 'Today' ? 'user' : 'contact',
  }));
}

/**
 * Convert EditorDocument array to TipTapDocument array for TipTapInput
 */
export function convertDocumentsToTipTap(documents: EditorDocument[]): TipTapDocument[] {
  return documents.map((doc) => ({
    id: doc.id,
    label: doc.title ?? doc.id,
    imageUrl: doc.imageUrl,
  }));
}

// ============================================================================
// Component
// ============================================================================

export function CadenceStepSectionPure({
  userTeam,
  contact,
  contactUser,
  step,
  atVariables,
  documents = [],
  fromEmailOptions = [],
  toEmailOptions = [],
  previousEmailThread = null,
  lastStepWasEmail = false,
  aiChoices = [],
  aiIndex = 0,
  aiCommand = '',
  aiLoading = false,
  stepsList = [],
  allowEditStepType = true,
  allowSaveAsTemplate = true,
  allowEmailDetails = true,
  disableLikeRecentPosts = false,
  autoGenerateAi = false,
  initiallyAnimate = false,
  sendMode,
  showSendModeDropdown = false,
  onSendModeChange,
  messageAttachment,
  texts,
  isSubmitting = false,
  isAiGenerating = false,
  isSavingSnippet = false,
  reminderActions = [],
  isLoadingReminderActions = false,
  onCreateReminderAction,
  isCreatingReminderAction = false,
  onSubmit,
  onCancel,
  onChange,
  onGenerateAi,
  onAiCommandChange,
  onAiIndexChange,
  onSaveSnippet,
  onToast,
  renderSubjectEditor,
  renderBodyEditor,
  renderSuggestTemplates,
  renderReminderInput,
  renderSelectDropdown,
  renderCharCounter,
  wrapperClassName,
  contentClassName,
}: CadenceStepSectionPureProps) {
  const subjectRef = useRef<Editor | null>(null);
  const bodyRef = useRef<Editor | null>(null);

  // Double-submit prevention: ref for synchronous check + local state for UI
  const isSubmittingRef = useRef(false);
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);

  // Local state
  const [changes, setChanges] = useState<StepChanges>({});
  const [wasTemplatePasted, setWasTemplatePasted] = useState(false);
  const [deferredStepType, setDeferredStepType] = useState<CadenceStepType | undefined>();
  const [fromEmailLabel, setFromEmailLabel] = useState<string>();

  // Derived state
  const stepType = changes.type ?? step.type;
  const stepBody = changes.body2 ?? step.body2 ?? '';
  const stepSubject = changes.subject ?? step.subject ?? '';
  const stepFrom = changes.from ?? step.from ?? '';
  const stepTo = changes.to ?? step.to ?? '';
  const stepCc = changes.cc ?? step.cc ?? '';
  const stepBcc = changes.bcc ?? step.bcc ?? '';

  // Cc/Bcc visibility. For email steps with editable details, the inputs auto-show
  // whenever the underlying value is non-empty (e.g. a prefilled Cc/Bcc, or the user
  // typing into one). We keep local `showCc`/`showBcc` state so the manual "Cc"/"Bcc"
  // buttons can reveal an empty input and `handleFocusBody` can hide empty ones — but
  // we re-sync that state to the value's emptiness whenever the value / step type /
  // allowEmailDetails changes. This is the adjust-during-render form of the old
  // `useEffect` (mirrors ColorInput): tracking the previous inputs with a ref and
  // re-syncing during render avoids the double-render + stale-flash an effect causes.
  const emailDetailsEditable = allowEmailDetails && stepType === 'email';
  const [showCcInput, setShowCcInput] = useState(() => (emailDetailsEditable ? isNotEmpty(stepCc) : false));
  const [showBccInput, setShowBccInput] = useState(() =>
    emailDetailsEditable ? isNotEmpty(stepBcc) : (userTeam.useContactEmailAsBcc ?? false),
  );
  const prevCcBccSyncRef = useRef({ stepCc, stepBcc, stepType, allowEmailDetails });
  if (
    prevCcBccSyncRef.current.stepCc !== stepCc ||
    prevCcBccSyncRef.current.stepBcc !== stepBcc ||
    prevCcBccSyncRef.current.stepType !== stepType ||
    prevCcBccSyncRef.current.allowEmailDetails !== allowEmailDetails
  ) {
    prevCcBccSyncRef.current = { stepCc, stepBcc, stepType, allowEmailDetails };
    if (emailDetailsEditable) {
      setShowCcInput(isNotEmpty(stepCc));
      setShowBccInput(isNotEmpty(stepBcc));
    }
  }
  // No "Only Open" (a.k.a. "Free only") control here. It is a CADENCE-step setting: the gate lives
  // in the scheduled executor (`cadenceStepUnipileExec.service.ts` skips with `profile_not_open`),
  // and this composer's immediate InMail send goes out through `sendMessageVia`, which has no such
  // parameter. The header used to render the chip anyway, and it was dead three ways over — the
  // displayed state read `step.onlyOpenLink` only (never the pending `changes`, so it could not
  // flip), `handleSubmit` never put it in the payload, and both wrappers' ActionStep converters
  // drop the field. Set it on the campaign step instead ("Free only" in `CTSContentEditingPure`).
  const useEmailSignature =
    stepType === 'email' &&
    (changes.useEmailSignature ?? step.useEmailSignature ?? userTeam.useEmailSignatureByDefault);

  // Email thread continuation. Offered whenever this user has an email thread with the contact —
  // presence is the gate, NOT the subject, because threading rides on the provider ids the server
  // stored, which exist independently of whether that email had a subject.
  //
  // It starts on whenever the last step run was that email (`lastStepWasEmail`) — the rep is
  // answering the conversation they're looking at. Same precedence shape as `useEmailSignature`
  // above: pending edit → the step's own seed → the data-derived default.
  const canContinueThread = stepType === 'email' && previousEmailThread != null;
  const threadContinued = canContinueThread && (changes.replyToPrevious ?? step.replyToPrevious ?? lastStepWasEmail);

  // The subject this reply INHERITS from the thread, or null when the thread has none to give.
  // Non-null is what lets the Subject field stand down in favour of the banner: the server reuses
  // this subject verbatim (`buildEmailReplyOptions` → `subjectOverride`), so the field would only
  // collect a value the send throws away. Null means the send falls back to whatever the rep types,
  // so the field has to stay — hiding it there is what produced "Missing subject for email".
  const threadSubject = threadContinued ? previousEmailThread.subject : null;

  const showSubject = (stepType ? ['email', 'inmail'].includes(stepType) : false) && threadSubject == null;
  const showBody = stepType ? ['email', 'inmail', 'message', 'call', 'connect'].includes(stepType) : false;
  const showAiSuggest = stepType ? ['email', 'inmail', 'connect', 'message'].includes(stepType) : false;
  const isPremium = userTeam.isPremium;

  const handleChanges = useCallback(
    (data: StepChanges) => {
      setChanges((current) => ({ ...current, ...data }));
      onChange(data);
    },
    [onChange],
  );

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Prevent double-submit: check ref synchronously (before async state updates)
    if (isSubmittingRef.current || isSubmitting) return;
    isSubmittingRef.current = true;
    setIsLocalSubmitting(true);

    try {
      if (stepType !== 'connect' && !isNotEmptyTipTap(stepBody)) {
        onToast?.('Message is required', 'error');
        return;
      }
      // A thread-continuing email inherits the previous thread's subject server-side, so it
      // legitimately has none of its own — the Subject field isn't even rendered in that state.
      // Only when there IS a subject to inherit, though: threading onto a subject-less thread still
      // needs one, which is exactly why `showSubject` keeps the field up in that case.
      if ((stepType === 'email' || stepType === 'inmail') && threadSubject == null && !isNotEmptyTipTap(stepSubject)) {
        onToast?.('Subject is required', 'error');
        return;
      }

      const params: StepData = {
        id: step.id,
        type: stepType,
        subject: stepSubject || null,
        body2: stepBody || null,
        from: stepFrom || null,
        to: stepTo || null,
        cc: stepCc || null,
        bcc: stepBcc || null,
        // The email header toggles travel WITH the submitted step. They used to reach the wrapper
        // only through the `onChange` side-channel, which the quick-action wrapper keeps in separate
        // state and never merges back in — so "Use Email Signature" was silently dropped on send.
        ...(stepType === 'email' ? { useEmailSignature, replyToPrevious: threadContinued } : {}),
      };

      await onSubmit(params);
    } finally {
      isSubmittingRef.current = false;
      setIsLocalSubmitting(false);
    }
  };

  const handleFocusBody = () => {
    if (stepType !== 'email') return;
    if (!isNotEmpty(stepCc)) setShowCcInput(false);
    if (!isNotEmpty(stepBcc)) setShowBccInput(false);
  };

  // Filter steps list based on contact state
  const filteredSteps = useMemo(() => {
    const connectedAt = contactUser.liConnectedAt;
    const professionalEmail = contact.primaryFields?.professionalEmail?.data;
    const phone = contact.primaryFields?.phone?.data;

    const disabledState: Partial<Record<CadenceStepType, boolean>> = {
      email: !isNotEmpty(professionalEmail),
      call: !isNotEmpty(phone),
      follow: connectedAt != null,
      connect: connectedAt != null,
      inmail: connectedAt != null,
      likeRecentPosts: disableLikeRecentPosts,
    };

    return stepsList.filter((s) => !disabledState[s.value]);
  }, [stepsList, contact, contactUser, disableLikeRecentPosts]);

  // Convert atVariables and documents for TipTapInput (used when render props not provided)
  const tipTapVariables = useMemo(() => convertAtVariablesToTipTap(atVariables), [atVariables]);
  const tipTapDocuments = useMemo(() => convertDocumentsToTipTap(documents), [documents]);

  // Ai auto-generate on mount
  useEffect(() => {
    if (autoGenerateAi && onGenerateAi) {
      void onGenerateAi(true);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a fresh AI generation result arrives, sync the selected choice into the
  // local `changes` state so the editor updates immediately. The parent owns the
  // generation mutation and updates its own copy of the body, but the editor's
  // `value` is driven by `stepBody` (derived from local `changes`), which the
  // parent can't reach. Without this, the generated message wouldn't show until
  // the user toggled a variant tab (which routes through `handleChanges`).
  const prevAiChoicesRef = useRef(aiChoices);
  useEffect(() => {
    if (aiChoices === prevAiChoicesRef.current) return;
    prevAiChoicesRef.current = aiChoices;
    const choice = aiChoices[aiIndex];
    if (!choice) return;
    handleChanges({ body2: choice.body, subject: choice.subject });
  }, [aiChoices, aiIndex, handleChanges]);

  const handleAiIndexChange = (newIndex: string) => {
    const idx = parseInt(newIndex, 10);
    if (idx === -1) {
      void onGenerateAi?.(true);
      return;
    }
    if (aiChoices.length > 0 && aiChoices[idx]) {
      handleChanges({
        body2: aiChoices[idx].body,
        subject: aiChoices[idx].subject,
      });
    }
    onAiIndexChange?.(idx);
  };

  const handleSaveSnippet = async () => {
    if (!onSaveSnippet) return;
    try {
      await onSaveSnippet();
      onToast?.('Template saved', 'success');
    } catch {
      // Parent's onSaveSnippet already handles error toasting
    }
  };

  const isBlankBody = (html: string | null | undefined) =>
    !html || html === '<p></p>' || html === '<div><br></div>';

  /**
   * Inserting a template must not destroy what the user has already written.
   *
   * An empty editor means "start from this template", so the body is replaced.
   * A non-empty one means "drop this in where I am", so the content goes in at
   * the cursor through the editor itself — which keeps TipTap's own undo stack
   * intact, unlike rewriting the HTML underneath it. The subject is only filled
   * when it's still blank, for the same reason.
   */
  const handleTemplateClick = (body2: string, subject?: string) => {
    const keepSubject = !isBlankBody(changes.subject ?? step.subject);
    const nextSubject = keepSubject ? undefined : subject;

    if (isBlankBody(stepBody)) {
      handleChanges({ body2, subject: nextSubject });
    } else {
      const editor = bodyRef.current;
      if (editor != null) {
        editor.chain().focus().insertContent(body2).run();
        handleChanges({ body2: editor.getHTML(), subject: nextSubject });
      } else {
        // No editor instance (a render-prop override supplied its own): append
        // rather than clobber, which is still non-destructive.
        handleChanges({ body2: `${stepBody}${body2}`, subject: nextSubject });
      }
    }
    setWasTemplatePasted(true);
  };

  const isEditorEmpty = isBlankBody(stepBody);
  const shouldShowSuggestions = showBody && !wasTemplatePasted && (isEditorEmpty || stepBody);

  return (
    <AnimatePresence>
      {step && stepType && (
        <motion.form
          key="section"
          initial={initiallyAnimate ? false : { y: '100%' }}
          animate={{ y: '0%' }}
          exit={{ y: '100%' }}
          transition={{ type: 'tween', duration: 0.15, ease: 'easeOut' }}
          className={cn('absolute inset-0 z-10 m-0 flex flex-col bg-white dark:bg-gray-900', wrapperClassName)}
          onSubmit={handleSubmit}>
          {/* Header */}
          <CadenceStepSectionHeaderPure
            stepType={stepType}
            filteredSteps={filteredSteps}
            allowEditStepType={allowEditStepType}
            useEmailSignature={useEmailSignature}
            canContinueThread={canContinueThread}
            threadContinued={threadContinued}
            threadInheritsSubject={threadSubject != null}
            deferredStepType={deferredStepType}
            onDeferredStepTypeChange={setDeferredStepType}
            onCancel={onCancel}
            onChange={handleChanges}
            renderSelectDropdown={renderSelectDropdown}
          />

          {/* AI Loading Bar */}
          {isAiGenerating && (
            <div className="h-1 bg-linear-to-r from-blue-400 via-purple-400 to-blue-400 bg-size-[200%_100%] animate-shimmer" />
          )}

          {/* AI Suggest */}
          {showAiSuggest && (
            <CadenceStepSectionAiSuggestPure
              isPremium={isPremium}
              aiCommand={aiCommand}
              aiChoices={aiChoices}
              aiIndex={aiIndex}
              aiLoading={aiLoading}
              hasBody={(stepBody?.length ?? 0) > 0}
              onCommandChange={onAiCommandChange}
              onSubmit={() => {
                void onGenerateAi?.();
              }}
              onIndexChange={handleAiIndexChange}
            />
          )}

          {/* Content */}
          <div className={cn('flex-1 flex flex-col overflow-hidden', contentClassName)}>
            {aiLoading && (
              <div className="flex items-center justify-center h-32">
                <WaveSpinner />
              </div>
            )}

            {!aiLoading && (showSubject || showBody) && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Email fields */}
                {stepType === 'email' && allowEmailDetails && renderSelectDropdown && (
                  <>
                    {/* From */}
                    {renderSelectDropdown?.({
                      options: fromEmailOptions,
                      value: stepFrom,
                      onChange: (option) => {
                        setFromEmailLabel(option.label);
                        handleChanges({ from: option.value });
                      },
                      placeholder: 'From',
                      leftTitle: 'From',
                      size: 'large',
                      children: <span>{fromEmailLabel || stepFrom}</span>,
                      optionRow: (opt) => (
                        <div className="flex items-center gap-2">
                          <span>{opt.label}</span>
                          {opt.helperText && (
                            <Badge size="sm" rounded type="standard">
                              {opt.helperText}
                            </Badge>
                          )}
                        </div>
                      ),
                    })}

                    {/* To with Cc/Bcc buttons */}
                    <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
                      {renderSelectDropdown?.({
                        options: toEmailOptions,
                        value: stepTo,
                        onChange: (option) => handleChanges({ to: option.value }),
                        placeholder: 'To',
                        leftTitle: 'To',
                        size: 'large',
                        children: <span>{stepTo}</span>,
                        optionRow: (opt) => (
                          <div className="flex items-center gap-2">
                            <span>{opt.label}</span>
                            {opt.helperText && (
                              <Badge size="sm" rounded type="standard">
                                {opt.helperText}
                              </Badge>
                            )}
                          </div>
                        ),
                      })}
                      <div className="flex gap-1 pr-2">
                        {!showCcInput && (
                          <Button onClick={() => setShowCcInput(true)} size="sm">
                            Cc
                          </Button>
                        )}
                        {!showBccInput && (
                          <Button onClick={() => setShowBccInput(true)} size="sm">
                            Bcc
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Cc */}
                    {showCcInput && (
                      <div className="flex items-center px-3 border-b border-gray-200 dark:border-gray-700">
                        <Input
                          className="w-full"
                          icon={<span>Cc</span>}
                          value={stepCc}
                          bordered={false}
                          onChange={(e) => handleChanges({ cc: e.target.value })}
                        />
                      </div>
                    )}

                    {/* Bcc */}
                    {showBccInput && (
                      <div className="flex items-center px-3 border-b border-gray-200 dark:border-gray-700">
                        <Input
                          className="w-full"
                          icon={<span>Bcc</span>}
                          value={stepBcc}
                          bordered={false}
                          onChange={(e) => handleChanges({ bcc: e.target.value })}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Thread banner — stands in for the Subject field while the send continues a
                    thread THAT HAS A SUBJECT TO GIVE. It sits in the Subject row's place and shows
                    the subject the send will actually go out with (the server reuses the thread's
                    subject verbatim — see `buildEmailReplyOptions`), so the rep never has to guess
                    which of the two is true: that the Subject field vanished, or that it was
                    inherited. A subject-less thread swaps the two back: no banner, Subject field
                    stays, and the toggle alone says the reply still lands in the thread.

                    Deliberately terser than the campaign editor's equivalent banner
                    (`CTSContentEditingPure`, which leads with "Continues the previous email
                    thread"): this composer renders at ~450px in the contact sidebar and the
                    extension sidepanel, where a leading sentence would eat the whole row and
                    truncate the subject itself. The header toggle directly above states the
                    behaviour ("Reply in thread", checked + filled); this row names the value. */}
                {threadSubject != null && (
                  <div
                    data-testid="step-thread-banner"
                    className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 text-sm dark:border-gray-700">
                    <Arrows.CornerUpLeft className="size-4 shrink-0 text-gray-400 dark:text-gray-500" />
                    <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-200">{threadSubject}</span>
                    <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">Thread subject</span>
                  </div>
                )}

                {/* Subject */}
                {showSubject && (
                  <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
                    {renderSubjectEditor &&
                      renderSubjectEditor({
                        value: stepSubject,
                        onChange: (v) => handleChanges({ subject: v }),
                        placeholder: 'Subject',
                        documents,
                        atVariables,
                        contactId: contact.id,
                        allowLinks: stepType === 'email',
                        autoFocus: true,
                        ref: subjectRef,
                      })}
                    {!renderSubjectEditor && (
                      <TipTapInput
                        ref={subjectRef}
                        className="flex-1"
                        editorClassName="min-h-[24px]"
                        placeholder="Subject"
                        value={stepSubject}
                        onChange={(html) => handleChanges({ subject: html })}
                        variables={tipTapVariables}
                        documents={tipTapDocuments}
                        allowLinks={stepType === 'email'}
                        autoFocus
                        size="sm"
                        bordered={false}
                      />
                    )}
                  </div>
                )}

                {/* Body */}
                {showBody && (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {renderBodyEditor &&
                      renderBodyEditor({
                        value: stepBody,
                        onChange: (v) => handleChanges({ body2: v }),
                        placeholder: stepType === 'call' ? 'Call script' : 'Body',
                        documents,
                        atVariables,
                        contactId: contact.id,
                        allowFormatting: stepType === 'email' || stepType === 'call',
                        allowLinks: stepType === 'email',
                        autoFocus: !showSubject,
                        onFocus: handleFocusBody,
                        ref: bodyRef,
                      })}
                    {!renderBodyEditor && (
                      <TipTapInput
                        ref={bodyRef}
                        className="flex-1 overflow-auto"
                        editorClassName="h-full"
                        placeholder={stepType === 'call' ? 'Call script' : 'Body'}
                        value={stepBody}
                        onChange={(html) => handleChanges({ body2: html })}
                        variables={tipTapVariables}
                        documents={tipTapDocuments}
                        allowFormatting={stepType === 'email' || stepType === 'call'}
                        allowLinks={stepType === 'email'}
                        autoFocus={!showSubject}
                        onFocus={handleFocusBody}
                        bordered={false}
                        shape="square"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Visit step */}
            {stepType === 'visit' && (
              <p className="p-4 text-center text-gray-500">Our extension will visit the lead&apos;s page</p>
            )}

            {/* Like posts step */}
            {stepType === 'likeRecentPosts' && (
              <div className="p-4">
                <div className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">Posts to like</div>
                <Input
                  placeholder="Posts to like"
                  onChange={(e) => handleChanges({ postsToLike: Number(e.target.value) })}
                  type="number"
                  value={changes.postsToLike ?? 3}
                />
              </div>
            )}

            {/* Reminder step */}
            {stepType === 'reminder' && (
              <div className="p-4">
                {renderReminderInput &&
                  renderReminderInput({
                    value: stepBody,
                    onChange: (v) => handleChanges({ body2: v }),
                    teamId: userTeam.teamId,
                  })}
                {!renderReminderInput && (
                  <ManualSelectPure
                    value={stepBody}
                    onChange={(v) => handleChanges({ body2: v })}
                    reminderActions={reminderActions}
                    isLoading={isLoadingReminderActions}
                    onCreateReminder={onCreateReminderAction}
                    isCreating={isCreatingReminderAction}
                    onError={(msg) => onToast?.(msg, 'error')}
                    fullWidth
                  />
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <CadenceStepSectionActionsPure
            stepType={stepType}
            stepBody={stepBody}
            stepSubject={stepSubject}
            isLoading={isSubmitting || isLocalSubmitting}
            isSavingSnippet={isSavingSnippet}
            allowSaveAsTemplate={allowSaveAsTemplate}
            wasTemplatePasted={wasTemplatePasted}
            showSendModeDropdown={showSendModeDropdown}
            sendMode={sendMode}
            texts={texts}
            onSendModeChange={onSendModeChange}
            onSaveSnippet={handleSaveSnippet}
            onShowSuggestions={() => setWasTemplatePasted(false)}
            renderSuggestTemplates={
              shouldShowSuggestions && renderSuggestTemplates
                ? () =>
                    renderSuggestTemplates({
                      value: stepBody,
                      atVariables,
                      documents,
                      onClick: handleTemplateClick,
                      forceShow: isEditorEmpty,
                    })
                : undefined
            }
            renderCharCounter={
              stepType === 'connect' && stepBody ? () => renderCharCounter?.({ body2: stepBody }) : undefined
            }
            messageAttachment={messageAttachment}
            onAttachmentError={(msg) => onToast?.(msg, 'error')}
          />
        </motion.form>
      )}

      {/* Step type change confirmation popup */}
      {deferredStepType && (
        <UpdateStepTypePopup
          onDelete={() => {
            handleChanges({ type: deferredStepType });
            setDeferredStepType(undefined);
          }}
          onClose={() => setDeferredStepType(undefined)}
          showActionCanNotBeUndone={false}
        />
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// Header Sub-Component
// ============================================================================

type HeaderPureProps = {
  stepType: CadenceStepType;
  filteredSteps: StepListItem[];
  allowEditStepType?: boolean;
  useEmailSignature?: boolean;
  canContinueThread?: boolean;
  threadContinued?: boolean;
  /** Whether the continued thread has a subject to inherit — decides what "on" actually means here. */
  threadInheritsSubject?: boolean;
  deferredStepType?: CadenceStepType;
  onDeferredStepTypeChange: (type: CadenceStepType | undefined) => void;
  onCancel?: () => void;
  onChange: (changes: StepChanges) => void;
  renderSelectDropdown?: CadenceStepSectionPureProps['renderSelectDropdown'];
};

function CadenceStepSectionHeaderPure({
  stepType,
  filteredSteps,
  allowEditStepType,
  useEmailSignature,
  canContinueThread,
  threadContinued,
  threadInheritsSubject,
  deferredStepType: _deferredStepType,
  onDeferredStepTypeChange,
  onCancel,
  onChange,
  renderSelectDropdown: _renderSelectDropdown,
}: HeaderPureProps) {
  useEscStack('CadenceStepSectionHeader', ESC_PRIORITY.MED, onCancel);

  const handleStepTypeChange = (value: CadenceStepType) => {
    if (stepType === 'email' && ['inmail', 'message', 'call', 'connect'].includes(value)) {
      onDeferredStepTypeChange(value);
    } else {
      onChange({ type: value });
    }
  };

  const currentStepLabel = filteredSteps.find((s) => s.value === stepType)?.labelFull;
  const stepTypeOptions = filteredSteps.map((s) => ({
    value: s.value,
    label: String(s.labelFull ?? s.label),
  }));

  return (
    <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
      {/* Wraps rather than overflows: this header renders at ~450px in the contact sidebar and the
          extension sidepanel, where email shows three controls. Without wrapping the last one is
          clipped and the close button is pushed off-screen entirely. */}
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {stepType && <StepIcon className="size-5" iconType={stepType} />}
        {allowEditStepType && filteredSteps.length > 1 && (
          <FilterSelect
            value={stepType}
            options={stepTypeOptions}
            onChange={(val: string) => handleStepTypeChange(val as CadenceStepType)}
            size="sm"
            bordered={false}
            className="font-medium"
          />
        )}
        {(!allowEditStepType || filteredSteps.length <= 1) && <span className="font-medium">{currentStepLabel}</span>}
        {stepType === 'email' && (
          <CheckboxButton
            checked={useEmailSignature === true}
            tooltip={
              useEmailSignature === true
                ? 'On — your email signature is appended to this send'
                : 'Off — this send goes out without your email signature'
            }
            onChange={(checked) => onChange({ useEmailSignature: checked })}>
            Use Email Signature
          </CheckboxButton>
        )}
        {canContinueThread && (
          <CheckboxButton
            checked={threadContinued === true}
            tooltip={
              threadContinued
                ? threadInheritsSubject
                  ? 'On — this reply lands in the last email thread and keeps that thread’s subject'
                  : // That thread has no subject of its own to inherit, so the Subject field stays up
                    // and what the rep writes there is what goes out. Saying "keeps that thread’s
                    // subject" here would tell them the field they are typing in is ignored.
                    'On — this reply lands in the last email thread, which has no subject of its own; ' +
                    'the subject you write is used'
                : 'Off — this send starts a brand-new email thread with its own subject'
            }
            onChange={(checked) => onChange({ replyToPrevious: checked })}>
            Reply in thread
          </CheckboxButton>
        )}
      </div>
      {onCancel && <Button className="shrink-0" text icon={<General.XMark />} onClick={onCancel} />}
    </div>
  );
}

// ============================================================================
// AI Suggest Sub-Component
// ============================================================================

type AiSuggestPureProps = {
  isPremium: boolean;
  aiCommand: string;
  aiChoices: AiChoice[];
  aiIndex: number;
  aiLoading: boolean;
  hasBody: boolean;
  onCommandChange?: (command: string) => void;
  onSubmit: () => void;
  onIndexChange: (index: string) => void;
};

function CadenceStepSectionAiSuggestPure({
  isPremium,
  aiCommand,
  aiChoices,
  aiIndex,
  aiLoading,
  hasBody,
  onCommandChange,
  onSubmit,
  onIndexChange,
}: AiSuggestPureProps) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-linear-to-r from-indigo-50/60 to-blue-50/60 dark:from-indigo-950/40 dark:to-blue-950/40 px-3.5 py-3 space-y-2">
      {isPremium && (
        <>
          <Input
            size="sm"
            value={aiCommand}
            onChange={(e) => onCommandChange?.(e.currentTarget.value)}
            placeholder={
              hasBody
                ? 'Improve message with AI or provide custom instructions'
                : 'Generate message with AI or provide custom instructions'
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                onSubmit();
              }
            }}
            disabled={aiLoading}
            className="w-full"
            icon={
              aiLoading ? (
                <WaveSpinner size={17} />
              ) : (
                <Tooltip
                  label="Press Enter or click to generate. Provide custom instructions for tailored results."
                  placement="top"
                  autoPlace={false}>
                  <button
                    type="button"
                    onClick={onSubmit}
                    className="flex items-center justify-center hover:scale-110 transition-transform">
                    <General.Zap className="size-3.5 text-indigo-400 dark:text-indigo-300" />
                  </button>
                </Tooltip>
              )
            }
          />
          {aiChoices.length > 0 && (
            <div className="flex justify-end">
              <ButtonSegment size="sm" selectedId={`${aiIndex}`} onChange={onIndexChange}>
                {aiChoices.map((_, index) => (
                  <Button key={index} id={`${index}`} type="button">
                    {index + 1}
                  </Button>
                ))}
                <Button id="-1" icon={<Arrows.RefreshCw04 />} type="button" />
              </ButtonSegment>
            </div>
          )}
        </>
      )}
      {!isPremium && (
        <Button size="sm" tooltip="AI is only for premium accounts, contact Poseidon to get a premium account">
          Upgrade for AI
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Actions Sub-Component
// ============================================================================

type ActionsPureProps = {
  stepType: CadenceStepType;
  stepBody: string;
  stepSubject: string;
  isLoading: boolean;
  isSavingSnippet: boolean;
  allowSaveAsTemplate?: boolean;
  wasTemplatePasted: boolean;
  showSendModeDropdown?: boolean;
  sendMode?: SendMode;
  texts?: { submit?: string };
  onSendModeChange?: (mode: SendMode) => void;
  onSaveSnippet: () => void;
  onShowSuggestions: () => void;
  renderSuggestTemplates?: () => ReactNode;
  renderCharCounter?: () => ReactNode;
  /** File attachment affordance (message/inmail only). Omitted → no attach button. */
  messageAttachment?: MessageAttachmentState;
  onAttachmentError?: (message: string) => void;
};

function CadenceStepSectionActionsPure({
  stepType,
  stepBody,
  stepSubject,
  isLoading,
  isSavingSnippet,
  allowSaveAsTemplate,
  wasTemplatePasted,
  showSendModeDropdown,
  sendMode,
  texts,
  onSendModeChange,
  onSaveSnippet,
  onShowSuggestions,
  renderSuggestTemplates,
  renderCharCounter,
  messageAttachment,
  onAttachmentError,
}: ActionsPureProps) {
  const handleSaveSnippetAllowed = stepType ? ['message', 'email', 'inmail', 'connect'].includes(stepType) : false;
  const isSubjectNotEmpty = isNotEmptyTipTap(stepSubject);
  const isBodyNotEmpty = isNotEmptyTipTap(stepBody);
  const allowAttachment = stepType === 'message' || stepType === 'inmail';

  return (
    <div className="flex flex-col pt-0.5 px-2 pb-2">
      {renderSuggestTemplates?.()}

      <div className="flex justify-between px-2 pt-2">
        <div className="flex flex-row items-center gap-2">
          {allowSaveAsTemplate && handleSaveSnippetAllowed && (isSubjectNotEmpty || isBodyNotEmpty) && (
            <Button
              onClick={onSaveSnippet}
              disabled={isSavingSnippet}
              tooltip="Save as a message template"
              status={isSavingSnippet ? 'updating' : undefined}
              icon={<General.PlusSquare />}
              color="gray"
              size="sm"
            />
          )}
          {messageAttachment && allowAttachment && (
            <MessageAttachmentButtonPure
              filename={messageAttachment.filename}
              onSelectFile={messageAttachment.onSelectFile}
              onRemove={messageAttachment.onRemove}
              isUploading={messageAttachment.isUploading}
              disabled={isLoading}
              onError={onAttachmentError}
            />
          )}
          {renderCharCounter?.()}
        </div>

        {wasTemplatePasted && (
          <Button onClick={onShowSuggestions} tooltip="Show suggestions" icon={<General.Eye />} size="sm">
            Show suggestions
          </Button>
        )}

        {showSendModeDropdown && onSendModeChange && (
          <ButtonGroup color="blue" size="sm">
            <Button type="submit" disabled={isLoading} icon={isLoading ? <WaveSpinner /> : <General.CheckCircle />}>
              {sendMode === 'scheduled' ? 'Schedule to Send' : 'Send'}
            </Button>
            <Button disabled={isLoading} className="relative px-1.5">
              <select
                value={sendMode}
                onChange={(e) => onSendModeChange(e.target.value as SendMode)}
                disabled={isLoading}
                className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed">
                <option value="scheduled">Schedule to Send</option>
                <option value="now">Send Now (ignore schedule)</option>
              </select>
              <Arrows.ChevronDown className="size-4" />
            </Button>
          </ButtonGroup>
        )}

        {!showSendModeDropdown && (
          <Button
            type="submit"
            color="blue"
            size="sm"
            disabled={isLoading}
            icon={isLoading ? <WaveSpinner /> : <General.CheckCircle />}>
            {texts?.submit ?? 'Send'}
          </Button>
        )}
      </div>
    </div>
  );
}
